import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

/**
 * useUrlFilters — global filter state hook shared across the CRM.
 *
 * Behavior:
 *  • Reads filter state from the URL search params on mount.
 *  • Falls back to sessionStorage under `key` when no URL state.
 *  • Pushes changes to both the URL (for share/bookmark) and sessionStorage
 *    (for tab lifetime) with debounce.
 *  • Multi-value filters are encoded as CSV (`?industry=Energy,Healthcare`)
 *    so the URL stays short.
 *
 * Config format:
 *   { industry: { type: 'multi' },
 *     q:        { type: 'string' },
 *     rel_min:  { type: 'number' } }
 */
const KEEP_KEYS_ON_SET_ALL = new Set(); // reserved for future non-filter URL params

function encodeValue(value, type) {
  if (value === undefined || value === null) return null;
  if (type === "multi") {
    if (!Array.isArray(value) || value.length === 0) return null;
    return value.join(",");
  }
  if (type === "number") {
    if (value === "" || Number.isNaN(Number(value))) return null;
    return String(value);
  }
  const s = String(value).trim();
  return s === "" ? null : s;
}

function decodeValue(raw, type) {
  if (raw === null || raw === undefined) return type === "multi" ? [] : "";
  if (type === "multi") return raw.split(",").map((v) => v.trim()).filter(Boolean);
  if (type === "number") return raw === "" ? "" : Number(raw);
  return raw;
}

/**
 * @param {string} key sessionStorage bucket (unique per page).
 * @param {Record<string,{type:'multi'|'string'|'number'}>} config
 */
export function useUrlFilters(key, config) {
  const [searchParams, setSearchParams] = useSearchParams();
  const keys = useMemo(() => Object.keys(config), [config]);

  // Build the initial state: URL first, then sessionStorage, then defaults.
  const [filters, setFiltersState] = useState(() => {
    const stored = (() => {
      try {
        const raw = window.sessionStorage.getItem(`filters:${key}`);
        return raw ? JSON.parse(raw) : null;
      } catch {
        return null;
      }
    })();
    const initial = {};
    for (const k of keys) {
      const type = config[k]?.type || "string";
      const fromUrl = searchParams.get(k);
      if (fromUrl !== null) {
        initial[k] = decodeValue(fromUrl, type);
      } else if (stored && Object.prototype.hasOwnProperty.call(stored, k)) {
        initial[k] = decodeValue(stored[k], type);
      } else {
        initial[k] = type === "multi" ? [] : "";
      }
    }
    return initial;
     
  });

  // Persist to URL + sessionStorage
  const sync = useCallback((next) => {
    const params = new URLSearchParams(searchParams);
    const bucket = {};
    for (const k of keys) {
      const type = config[k]?.type || "string";
      const encoded = encodeValue(next[k], type);
      if (encoded === null) {
        params.delete(k);
      } else {
        params.set(k, encoded);
        bucket[k] = encoded;
      }
    }
    // Preserve non-filter params
    for (const [pk] of searchParams.entries()) {
      if (KEEP_KEYS_ON_SET_ALL.has(pk)) {
        params.set(pk, searchParams.get(pk));
      }
    }
    setSearchParams(params, { replace: true });
    try {
      window.sessionStorage.setItem(`filters:${key}`, JSON.stringify(bucket));
    } catch {
      // ignore quota errors
    }
     
  }, [config, keys, searchParams, setSearchParams, key]);

  // Debounced sync so rapid keystrokes on a text input don't spam history
  const debounceRef = useRef(null);
  useEffect(() => {
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => sync(filters), 200);
    return () => window.clearTimeout(debounceRef.current);
    // `sync` is intentionally omitted: its identity changes on every URL write
    // (it closes over searchParams), which would reset the debounce timer and
    // can loop. The latest sync is always used because the timer re-arms on
    // each filters change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const setFilter = useCallback((k, value) => {
    setFiltersState((prev) => ({ ...prev, [k]: value }));
  }, []);

  const setMany = useCallback((patch) => {
    setFiltersState((prev) => ({ ...prev, ...patch }));
  }, []);

  const reset = useCallback(() => {
    const empty = {};
    for (const k of keys) empty[k] = (config[k]?.type || "string") === "multi" ? [] : "";
    setFiltersState(empty);
  }, [config, keys]);

  // Query-string builder for axios `params` — always uses CSV so
  // repeated-param decoding is not required server-side, and FastAPI
  // still accepts the CSV via our parse_multi helper.
  const toParams = useCallback(() => {
    const out = {};
    for (const k of keys) {
      const type = config[k]?.type || "string";
      const encoded = encodeValue(filters[k], type);
      if (encoded !== null) out[k] = encoded;
    }
    return out;
  }, [config, keys, filters]);

  // Truthy when any filter has a non-empty value
  const activeCount = useMemo(
    () => keys.reduce((acc, k) => acc + (encodeValue(filters[k], config[k]?.type || "string") ? 1 : 0), 0),
    [config, keys, filters],
  );

  return {
    filters,
    setFilter,
    setMany,
    reset,
    toParams,
    activeCount,
  };
}

export default useUrlFilters;
